import { computed, ref, watch } from "vue";
import Swal from "sweetalert2";
import { useI18n } from "vue-i18n";

import brandService, { type Brand } from "../services/brand";
import { buildAssetUrl } from "../utils/settings";

type BrandForm = Omit<Brand, "image"> & {
  image?: string | File;
};

type BrandSaveError = {
  response?: {
    status?: number;
    data?: {
      errors?: Record<string, string[]>;
      message?: string;
    };
  };
};

export function useBrand() {
  const { t } = useI18n();
  const brands = ref<Brand[]>([]);
  const searchQuery = ref("");
  const showModal = ref(false);
  const selectedBrand = ref<Brand | null>(null);
  const loading = ref(false);
  const errors = ref<Record<string, string[]>>({});
  const previewUrl = ref("");

  const form = ref<BrandForm>({
    code: "",
    name: "",
    description: "",
    status: "1",
  });

  const fetchBrands = async () => {
    loading.value = true;
    try {
      brands.value = await brandService.getBrands();
    } catch (error) {
      console.error("Failed to fetch brands:", error);
      Swal.fire({
        icon: "error",
        title: t("brand_page.load_failed"),
        text: t("brand_page.load_failed_text"),
      });
    } finally {
      loading.value = false;
    }
  };

  const filteredBrands = computed(() => {
    const query = searchQuery.value.trim().toLowerCase();
    if (!query) return brands.value;

    return brands.value.filter((brand) =>
      [brand.code, brand.name, brand.description]
        .filter(Boolean)
        .some((value) => String(value).toLowerCase().includes(query)),
    );
  });

  const pageSizeOptions = ref(["10", "25", "50", "100", "1000"]);

  const paginationConfig = computed(() => ({
    showSizeChanger: true,
    pageSizeOptions: pageSizeOptions.value,
    showTotal: (total: number, range: [number, number]) =>
      t("brand_page.pagination_total", {
        start: range[0],
        end: range[1],
        total,
      }),
    defaultPageSize: 10,
    locale: { items_per_page: t("brand_page.items_per_page") },
  }));

  watch(
    filteredBrands,
    (newValue) => {
      const totalCount = newValue.length > 0 ? newValue.length : 1000;
      pageSizeOptions.value = ["10", "25", "50", "100", String(totalCount)];
    },
    { immediate: true },
  );

  const resetForm = () => {
    form.value = {
      code: "",
      name: "",
      description: "",
      status: "1",
    };
    errors.value = {};
    previewUrl.value = "";
  };

  const openCreateModal = () => {
    selectedBrand.value = null;
    resetForm();
    showModal.value = true;
  };

  const openEditModal = (brand: Brand) => {
    selectedBrand.value = brand;
    errors.value = {};
    form.value = {
      id: brand.id,
      code: brand.code || "",
      name: brand.name || "",
      description: brand.description || "",
      status: String(brand.status ?? "1"),
      image: brand.image,
    };
    previewUrl.value = typeof brand.image === "string" ? buildAssetUrl(brand.image) : "";
    showModal.value = true;
  };

  const closeModal = () => {
    showModal.value = false;
    previewUrl.value = "";
    errors.value = {};
  };

  const updateField = (field: keyof BrandForm, value: string | number | undefined) => {
    form.value = {
      ...form.value,
      [field]: value,
    };
  };

  const handleFileUpload = (event: Event) => {
    const target = event.target as HTMLInputElement | null;
    const file = target?.files?.[0] ?? null;

    if (!file) {
      if (!selectedBrand.value?.image) {
        previewUrl.value = "";
      }
      return;
    }

    form.value.image = file;
    previewUrl.value = URL.createObjectURL(file);
  };

  const removeImage = () => {
    form.value.image = undefined;
    previewUrl.value = "";
  };

  const generateBrandCode = async () => {
    try {
      if (!brands.value.length) {
        await fetchBrands();
      }

      const nextNumber =
        brands.value
          .map((item) => {
            const match = String(item.code || "").match(/(\d+)\s*$/);
            return match ? Number(match[1]) : 0;
          })
          .reduce((max, current) => Math.max(max, current), 0) + 1;

      form.value.code = `BRD${String(nextNumber).padStart(3, "0")}`;
    } catch (error) {
      console.error("Failed to generate brand code:", error);
      Swal.fire({
        icon: "error",
        title: t("brand_page.generate_failed"),
        text: t("brand_page.generate_failed_text"),
      });
    }
  };

  const saveBrand = async () => {
    loading.value = true;
    errors.value = {};

    try {
      const payload = new FormData();
      payload.append("code", form.value.code || "");
      payload.append("name", form.value.name || "");
      payload.append("description", form.value.description || "");
      payload.append("status", String(form.value.status ?? "1"));

      if (form.value.image instanceof File) {
        payload.append("image", form.value.image);
      }

      if (selectedBrand.value?.id) {
        await brandService.updateBrand(selectedBrand.value.id, payload);
      } else {
        await brandService.createBrand(payload);
      }

      await fetchBrands();
      await Swal.fire({
        icon: "success",
        title: selectedBrand.value ? t("brand_page.updated_title") : t("brand_page.created_title"),
        timer: 1400,
        showConfirmButton: false,
      });
      closeModal();
    } catch (error: unknown) {
      const requestError = error as BrandSaveError;
      console.error(error);
      if (requestError.response?.status === 422) {
        errors.value = requestError.response.data?.errors || {};
      } else {
        Swal.fire({
          icon: "error",
          title: t("brand_page.save_failed"),
          text: requestError.response?.data?.message || t("brand_page.save_failed_text"),
        });
      }
    } finally {
      loading.value = false;
    }
  };

  const deleteBrand = async (brand: Brand) => {
    if (!brand?.id) return;

    const result = await Swal.fire({
      title: t("brand_page.delete_title"),
      text: t("brand_page.delete_text", { name: brand.name }),
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: t("brand_page.delete_confirm"),
      cancelButtonText: t("common.cancel"),
      reverseButtons: true,
    });

    if (!result.isConfirmed) return;

    try {
      Swal.fire({
        title: t("brand_page.deleting_title"),
        text: t("brand_page.deleting_text"),
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      await brandService.deleteBrand(brand.id);
      await fetchBrands();

      Swal.fire({
        icon: "success",
        title: t("brand_page.deleted_title"),
        text: t("brand_page.deleted_text", { name: brand.name }),
        timer: 1500,
        showConfirmButton: false,
      });
    } catch (error) {
      console.error(error);
      Swal.fire({
        icon: "error",
        title: t("brand_page.delete_failed"),
        text: t("brand_page.delete_failed_text"),
      });
    }
  };

  return {
    searchQuery,
    filteredBrands,
    showModal,
    selectedBrand,
    form,
    errors,
    loading,
    previewUrl,
    paginationConfig,
    fetchBrands,
    openCreateModal,
    openEditModal,
    closeModal,
    updateField,
    handleFileUpload,
    removeImage,
    generateBrandCode,
    saveBrand,
    deleteBrand,
  };
}
